import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  useRepsProductModal,
  useRepsProductWarningModal,
} from "./modals/RepsProductModal";
import { selectUser } from "@/global/store/data/user";
import {
  recipientUserState,
  selectRecipientUser,
} from "@/global/store/data/purchaseSeats/recipientUser";
import { getCustomerAccountApi } from "@/global/api/auth";
import { parseApiErrorMessage } from "@/global/utils/utils";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppStore, useAppDispatch } from "@/global/store";
import { handleFormatPhoneNumber } from "@/global/utils/utils";

export type ValidateAccountIdProps = {
  accountId: string;
};

export type ConfirmAccountProps = {
  phoneNumber: string;
};

export function useRepsProduct() {
  // store
  const dispatch = useAppDispatch();
  const user = useAppStore(selectUser);
  const repsProductWarningModal = useRepsProductWarningModal();
  const getCustomerAccount = useMutation(getCustomerAccountApi);

  // config
  const repsProductModal = useRepsProductModal();
  const { teamSchema, purchaseSeatConfigs } = useTeamConfig();
  const isRepProduct = purchaseSeatConfigs?.isRepProduct ?? false;

  // states
  const recipientUser = useAppStore(selectRecipientUser);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [getCustomerAccountError, setGetCustomerAccountError] = useState<string>(""); // prettier-ignore

  /** modal handlers */
  function handleNextStep() {
    setActiveStep((prev) => prev + 1);
  }

  function handleFailedStep(step: number) {
    return step === 0;
  }

  function handleBackStep() {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  function checkIsRepProduct(): void {
    if (isRepProduct && !repsProductModal.hasModalOpened) {
      repsProductModal.set({ isOpen: true });
    }
  }

  function handleResetErrorMessage(): void {
    setGetCustomerAccountError("");
  }
  /** modal handlers */

  /** form handlers */
  async function handleValidateAccountId({
    accountId,
  }: ValidateAccountIdProps): Promise<void> {
    handleResetErrorMessage();

    try {
      const payload = { teamSchema, accountId: Number(accountId) };
      const { data } = await getCustomerAccount.mutateAsync(payload);
      dispatch(
        recipientUserState.actions.set({
          ...data,
          phoneNumber: handleFormatPhoneNumber(data.phoneNumber),
        })
      );

      // goes to next step
      handleNextStep();
    } catch (error) {
      setGetCustomerAccountError(parseApiErrorMessage(error));
    }
  }

  function handleConfirmAccount({ phoneNumber }: ConfirmAccountProps): void {
    dispatch(
      recipientUserState.actions.set({
        ...recipientUser,
        phoneNumber: handleFormatPhoneNumber(phoneNumber),
      })
    );
    repsProductModal.set({ isOpen: false, hasModalOpened: true });
  }
  /** form handlers */

  function handleCheckShouldOpenRepsProductModal(): boolean {
    if (isRepProduct && !repsProductWarningModal.hasModalOpened && !user) {
      return true;
    }
    return false;
  }

  return {
    states: {
      activeStep,
      isRepProduct,
      recipientUser,
      repsProductModal,
      repsProductWarningModal,
      getCustomerAccountError,
    },
    handlers: {
      handleBackStep,
      handleFailedStep,
      checkIsRepProduct,
      handleConfirmAccount,
      handleValidateAccountId,
      handleResetErrorMessage,
      handleCheckShouldOpenRepsProductModal,
    },
  };
}
